import { useRef } from 'react';
import classNames from 'classnames';

import { Accordion } from '@jsmdg/yoshi';
import { type Location } from '../../../shared/types';
import { type IsoCountryCode2Alpha } from '../../helper/iso3166Utils';
import { Flag } from '../Flag';
import { sortAndGroupVenues } from './sortAndGroupVenues';
import { trackExpandCountryLocationsClicked } from './venues.tracking';
import { VenuesListItem } from './VenuesListItem';
import styles from './VenuesList.module.scss';

type VenuesListProps = {
    readonly locations: Location[];
    readonly onVenueClicked: (venueId: string) => void;
    readonly className?: string;
    readonly activeVenue?: Location;
};

const VenuesList = ({
    activeVenue,
    className = '',
    locations,
    onVenueClicked,
}: VenuesListProps): JSX.Element => {
    const sortedAndGroupedVenues = Array.from(sortAndGroupVenues(locations));
    const cachedCountryRef = useRef<string | undefined>(activeVenue?.country);

    const toggleExpandedCountry = (countryCode: string, displayName: string): void => {
        const nextExpandedCountryCode =
            cachedCountryRef.current === countryCode ? undefined : countryCode;
        trackExpandCountryLocationsClicked(nextExpandedCountryCode, displayName);
        cachedCountryRef.current = nextExpandedCountryCode;
    };

    const collapsibleItems = sortedAndGroupedVenues.map(([countryCode, country]) => ({
        countryCode,
        ...country,
        caption: (
            <div>
                <Flag
                    countryCode={countryCode as keyof typeof IsoCountryCode2Alpha}
                    className="mr-1x"
                />
                <span className="fw-semibold">{country.displayName}</span>
            </div>
        ),
        body: (
            <>
                {Array.from(country.states).map(([state, venues]) => (
                    <div key={state} className="mt-1x">
                        <div className="theme-text-body-highlight">{state}</div>
                        <ul className="p-0">
                            {venues.map(venue => (
                                <VenuesListItem
                                    activeVenue={activeVenue as Location}
                                    onVenueClicked={onVenueClicked}
                                    venue={venue}
                                    key={venue.id}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </>
        ),
    }));

    return (
        <div className={classNames(styles.venuesListWrapper, 'px-2x h-100', className)}>
            <Accordion>
                {collapsibleItems.map(item => {
                    return (
                        <Accordion.Item
                            key={item.countryCode}
                            id={item.countryCode}
                            title={item.caption}
                            onClick={() =>
                                toggleExpandedCountry(item.countryCode, item.displayName)
                            }
                        >
                            {item.body}
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </div>
    );
};

export { VenuesList };
